'use client';

import { Suspense, startTransition, useRef, useState } from 'react';

import dynamic from 'next/dynamic';

import type { PopStoreExtendedSubscriptionLevel } from '@ps/features/subscription/types/SubscriptionLevel';
import { Popover, PopoverContent, PopoverTrigger } from '@ps/ui/atoms/popover';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { useToggleMemberBlock } from '../../hooks/useToggleMemberBlock';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import { ChannelAvatar } from '../common/ChannelAvatar';
import { BlockMemberDialog } from './BlockMemberDialog';
import { CommunityChannelMessageUserCard } from './CommunityChannelMessageUserCard';

// The chat window drags in the whole messaging/websocket stack (ChatRoom +
// ChatGlobalProvider). It's owner-only and interaction-gated, so keep it out of
// the (visitor) community bundle behind a lazy boundary — loaded the first time
// the owner opens a DM.
const importChatWindow = () => import('./MemberChatWindow');
const MemberChatWindow = dynamic(() =>
  importChatWindow().then(mod => mod.MemberChatWindow),
);

const AVATAR_SIZE = 28;
// Grace period before a hover-out closes the card, so moving the pointer from
// the avatar onto the (offset) card doesn't dismiss it.
const HOVER_CLOSE_DELAY_MS = 160;

interface Props {
  message: ExtendedCommunityChannelMessage;
  /** The author's resolved display name (computed by the bubble). */
  displayName: string;
  currentUserId: string | null | undefined;
}

/**
 * The message author's avatar as the trigger for the owner's user card (a
 * floating popover) — the local port of PopshopRN's user-card interaction. The
 * card only opens for the channel OWNER (moderation surface); everyone else sees
 * a plain avatar. Opens on hover (desktop) or tap (touch); from it the owner can
 * open a transient DM window ("Send Message") or block/unblock a non-owner
 * member.
 */
export function MessageAuthorCard({
  message,
  displayName,
  currentUserId,
}: Props) {
  const { canManage, creatorId, bioContext } = useCommunityContext();
  const author = message.profile;

  const [cardOpen, setCardOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Latches on the first "Send Message" so the lazy chat chunk isn't fetched
  // until the owner actually opens a DM; once mounted Radix owns open/close.
  const [chatMounted, setChatMounted] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  // Assume not-blocked on open (the card surfaces from a VISIBLE member's
  // message; a blocked member's messages are filtered out). Flips on toggle so a
  // block+unblock within the session reads correctly.
  const [blocked, setBlocked] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toggleBlock = useToggleMemberBlock({
    channelId: message.channelId,
    memberId: message.userId,
  });

  const avatar = (
    <ChannelAvatar src={author.avatar} name={displayName} size={AVATAR_SIZE} />
  );

  // The card is an owner-only moderation surface (block/unblock, DM). Anyone
  // without management on this surface — visitors AND an owner previewing their
  // own bio — gets a plain avatar.
  if (!canManage) {
    return avatar;
  }

  const isSelf = message.userId === currentUserId;
  // Owner can moderate any author that ISN'T the creator themselves.
  const canModerate = creatorId != null && message.userId !== creatorId;

  const cancelScheduledClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const openCard = () => {
    cancelScheduledClose();
    // Warm the lazy chat chunk the moment the owner opens the card, so
    // "Send Message" mounts it from cache instead of triggering an
    // on-demand chunk fetch (whose first-load recovery hard-reloads the page).
    void importChatWindow();
    setCardOpen(true);
  };
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(
      () => setCardOpen(false),
      HOVER_CLOSE_DELAY_MS,
    );
  };
  const handleOpenChange = (next: boolean) => {
    // Covers the tap/click path (touch fires no mouseenter) — warm the chunk
    // as the card opens.
    if (next) void importChatWindow();
    setCardOpen(next);
  };

  return (
    <>
      <Popover open={cardOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`View ${displayName}`}
            onMouseEnter={openCard}
            onMouseLeave={scheduleClose}
            // Fixed avatar box + `overflow-hidden` so the image can never
            // escape its 28px slot regardless of intrinsic size.
            className="focus-visible:ring-raise/40 inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full outline-none focus-visible:ring-2"
          >
            {avatar}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="start"
          sideOffset={6}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
          // Hover-opening shouldn't yank focus away from wherever the owner was.
          onOpenAutoFocus={event => event.preventDefault()}
          className="w-auto border-0 bg-transparent p-0 shadow-none"
        >
          <CommunityChannelMessageUserCard
            displayName={displayName}
            avatar={author.avatar}
            subscriptionLevel={
              message.subscriptionLevel as
                | PopStoreExtendedSubscriptionLevel
                | undefined
            }
            isSelf={isSelf}
            canModerate={canModerate}
            blocked={blocked}
            onSendMessage={() => {
              setCardOpen(false);
              // Mount the lazy chat window in a transition so React keeps the
              // channel visible while the chunk resolves instead of swapping in
              // a Suspense fallback.
              startTransition(() => {
                setChatMounted(true);
                setChatOpen(true);
              });
            }}
            onBlock={() => {
              setCardOpen(false);
              setBlockOpen(true);
            }}
            onClose={() => setCardOpen(false)}
          />
        </PopoverContent>
      </Popover>

      {chatMounted ? (
        // Local boundary: contain the lazy chunk's suspension here so it never
        // bubbles to the channel / page Suspense boundary.
        <Suspense fallback={null}>
          <MemberChatWindow
            open={chatOpen}
            onOpenChange={setChatOpen}
            targetUserId={message.userId}
            targetUser={{ name: displayName, avatar: author.avatar }}
            bioContext={bioContext}
          />
        </Suspense>
      ) : null}

      {canModerate ? (
        <BlockMemberDialog
          open={blockOpen}
          onOpenChange={setBlockOpen}
          blocked={blocked}
          memberName={displayName}
          onConfirm={async () => {
            await toggleBlock();
            setBlocked(value => !value);
          }}
        />
      ) : null}
    </>
  );
}
